import { createSlice } from '@reduxjs/toolkit';
import { candidateActions } from '../actions';
import { channelTypes, candidateStatusTypes } from 'appConstants';

const initialState = {
	loading: false,
	saving: false,
	isUpdating: false,
	phone: {},
	jobs: [],
	doNotText: false,
	status: candidateStatusTypes.ACTIVE,
	defaultChannel: channelTypes.SMS,
	availableChannels: [],
	searchCandidate: {
		isLoading: true,
		page: 1,
		candidates: []
	},
	currentCandidateCount: 0,

	// Used for integrations: Taleo, Bullhorn, etc.
	loadingExternalData: false,

	// ICIMS specific
	loadingICIMSData: false,
	icimsIntegrationData: {},

	// templates
	loadingTemplates: true,
	hasTemplates: false,
	teamTemplates: [],
	userTemplates: [],
	whatsAppTemplates: []
};

const setCandidate = (state, candidateData) => {
	/* eslint-disable dot-notation */
	state['id'] = candidateData.id;
	state['status'] = candidateData.status;
	state['firstName'] = candidateData.firstName;
	state['lastName'] = candidateData.source === 'nexxt' ? '(Restricted)' : candidateData.lastName;
	state.phone['number'] =
		candidateData.source === 'nexxt' ? '(Restricted)' : candidateData.phoneNumber;
	state['jobTitle'] = candidateData.jobTitle || '';
	state['defaultChannel'] = candidateData.channel || 'sms';
	state['availableChannels'] = candidateData.availableChannels || [channelTypes.SMS];
	state['doNotText'] = Boolean(candidateData.doNotText);
	state['jobs'] = (candidateData.jobs || []).slice().reverse();
	state['userName'] = candidateData.userName;
	// eslint-disable-next-line no-undefined
	state['optedIn'] = candidateData.optedIn === undefined ? true : candidateData.optedIn;
	if (candidateData.validation) {
		state.phone['formatted'] = candidateData.validation.formatNational;
		state.phone['lineType'] = candidateData.validation.trLineType;
		state.phone['status'] = candidateData.validation.trStatus;
		state.phone['regionCode'] = candidateData.validation.regionCode;
	}
};

const updateSearchCandidateLoop = (candidates, normalizedCandidates) => {
	candidates.forEach((c) => {
		const normalized = { phone: {} };
		normalized['id'] = c.id;
		normalized['status'] = c.status;
		normalized['firstName'] = c.firstName;
		normalized['lastName'] = c.source === 'nexxt' ? '(Restricted)' : c.lastName;
		normalized.phone['number'] = c.source === 'nexxt' ? '(Restricted)' : c.phoneNumber;
		normalized['jobTitle'] = c.jobTitle || '';
		normalized['defaultChannel'] = c.channel || channelTypes.SMS;
		normalized['availableChannels'] = c.availableChannels || [channelTypes.SMS];
		normalized['doNotText'] = Boolean(c.doNotText);
		normalized['jobs'] = c.jobs || [];
		normalized['userName'] = c.userName;
		if (c.validation) {
			normalized.phone['formatted'] = c.validation.formatNational;
			normalized.phone['lineType'] = c.validation.trLineType;
			normalized.phone['status'] = c.validation.trStatus;
			normalized.phone['regionCode'] = c.validation.regionCode;
		}
		normalizedCandidates.push(normalized);
	});
};

const updateSearchCandidate = (state, page, candidates) => {
	if (candidates.length) {
		let res = [];
		// eslint-disable-next-line no-undefined
		if (state.searchCandidate[page] === undefined) {
			state.searchCandidate[page] = [];
		}

		const normalizedCandidates = [];

		updateSearchCandidateLoop(candidates, normalizedCandidates);

		state.searchCandidate[page] = normalizedCandidates;
		for (let p = 1; p < page + 1; p++) {
			res = res.concat(state.searchCandidate[p]);
		}
		return res;
	}
	return state.searchCandidate.candidates;
};

const resetCandidate = (state) => {
	state.id = null;
	state.status = 'active';
	state['firstName'] = null;
	state['lastName'] = null;
	state.phone = {};
	state['jobTitle'] = null;
	state.defaultChannel = 'sms';
	state.availableChannels = [];
	state.doNotText = false;
	state.jobs = [];
	state['userName'] = null;
	state['optedIn'] = true;
	state.loading = false;
	state.saving = false;
	state.phone = {};
	state.isUpdating = false;
	state.jobs = [];
	state.doNotText = false;
	state.status = candidateStatusTypes.ACTIVE;
	state.defaultChannel = channelTypes.SMS;
	state.currentCandidateCount = 0;
	state.loadingExternalData = false;
	state.loadingICIMSData = false;
	state.icimsIntegrationData = {};
	state.loadingTemplates = true;
	state.hasTemplates = false;
	state.teamTemplates = [];
	state.userTemplates = [];
	state.whatsAppTemplates = [];
};

const candidateRejected = (state, action) => {
	state.loading = false;
	state.error = action.payload.error;
	resetCandidate(state);
};

const candidateSlice = createSlice({
	name: 'candidate',
	initialState,
	reducers: {},
	extraReducers: {
		[candidateActions.resetState]: (state) => resetCandidate(state),
		[candidateActions.fetchOrAdd.pending]: (state) => {
			state.loading = true;
		},
		[candidateActions.fetchOrAdd.fulfilled]: (state, action) => {
			state.loading = false;
			setCandidate(state, action.payload.candidate);
		},
		[candidateActions.fetchOrAdd.rejected]: (state, action) => {
			state.loading = false;
			state.error = action.payload.error;
		},

		// getcandidate
		[candidateActions.fetchCandidates.pending]: (state) => {
			state.loading = true;
		},
		[candidateActions.fetchCandidates.fulfilled]: (state, action) => {
			const { page } = action.payload.paging;
			const retrievedCandidates = action.payload.candidates || [];
			state.loading = false;
			state.currentCandidateCount = retrievedCandidates.length;
			state.searchCandidate.isLoading = false;
			state.searchCandidate.page = page;
			state.searchCandidate.candidates = updateSearchCandidate(state, page, retrievedCandidates);
		},
		[candidateActions.fetchCandidates.rejected]: (state, action) => {
			state.loading = false;
			state.error = action.payload.error;
		},
		[candidateActions.search.pending]: (state) => {
			state.loading = true;
		},
		[candidateActions.search.fulfilled]: (state, action) => {
			state.loading = false;
			state.searchCandidate.candidates = action.payload.candidates || [];
			state.searchCandidate.page = action.payload.paging.page;
		},
		// eslint-disable-next-line no-unused-vars
		[candidateActions.search.rejected]: (state, action) => {
			state.loading = false;
			state.searchCandidate.candidates = [];
			state.error = action.payload.error;
		},
		[candidateActions.fetchById.pending]: (state) => {
			state.loading = true;
		},
		[candidateActions.fetchById.fulfilled]: (state, action) => {
			state.loading = false;
			setCandidate(state, action.payload.candidate);
		},
		[candidateActions.fetchById.rejected]: (state, action) => {
			state.loading = false;
			state.error = action.payload.error;
			resetCandidate(state);
		},
		[candidateActions.fetchByPhone.pending]: (state) => {
			state.loading = true;
		},
		[candidateActions.fetchByPhone.fulfilled]: (state, action) => {
			state.loading = false;
			setCandidate(state, action.payload.candidate);
		},
		[candidateActions.fetchByPhone.rejected]: (state, action) => {
			candidateRejected(state, action);
		},
		[candidateActions.fetchBySource.pending]: (state) => {
			state.loading = true;
		},
		[candidateActions.fetchBySource.fulfilled]: (state, action) => {
			state.loading = false;
			setCandidate(state, action.payload.candidate);
		},
		[candidateActions.fetchBySource.rejected]: (state, action) => {
			candidateRejected(state, action);
		},

		// messageTemplate
		[candidateActions.fetchICIMSData.pending]: (state) => {
			state.loadingICIMSData = true;
		},
		[candidateActions.fetchICIMSData.fulfilled]: (state, action) => {
			state.loadingICIMSData = false;
			if (
				// eslint-disable-next-line no-prototype-builtins
				!action.payload.hasOwnProperty('$$hashKey') ||
				// eslint-disable-next-line no-undef
				(integrationData && Object.keys(integrationData).length)
			) {
				state.icimsIntegrationData = action.payload.integrationData;
			}
		},
		[candidateActions.fetchICIMSData.rejected]: (state, action) => {
			state.loadingICIMSData = false;
			state.candidate.icimsIntegrationData = {};
			state.error = action.payload.error;
		},

		[candidateActions.fetchAllTemplates.pending]: (state) => {
			state.loadingTemplates = true;
		},
		[candidateActions.fetchAllTemplates.fulfilled]: (state, action) => {
			const templates = action.payload.templates || [];
			const userTemps = [];
			const teamTemps = [];
			const whatsAppTemps = [];
			templates.forEach((template) => {
				const code = (template.integrationCode || '').toLowerCase();
				if (code === 'whatsapp') {
					whatsAppTemps.push(template);
				} else if (template.userId) {
					userTemps.push(template);
				} else {
					teamTemps.push(template);
				}
			});

			state.loadingTemplates = false;
			state.hasTemplates = Boolean(userTemps.length + teamTemps.length + whatsAppTemps.length);
			state.userTemplates = userTemps;
			state.teamTemplates = teamTemps;
			state.whatsAppTemplates = whatsAppTemps;
		},
		[candidateActions.fetchAllTemplates.rejected]: (state, action) => {
			state.loadingTemplates = false;
			state.error = action.payload.message;
		},
		[candidateActions.updateChannel.pending]: (state) => {
			state.isUpdating = true;
		},
		[candidateActions.updateChannel.fulfilled]: (state, action) => {
			state.isUpdating = false;
			state.defaultChannel = action.payload.candidate.channel;
		},
		[candidateActions.updateChannel.rejected]: (state) => {
			state.isUpdating = false;
		},
		[candidateActions.updateDoNotTextFlag]: (state, action) => {
			if (state.id === action.payload.candidateId) {
				let shouldNotText = state.doNotText;
				const mText = action.payload.messageText || '';
				if (action.payload.doNotTextFlag) {
					shouldNotText = true;
				} else if (mText.toLowerCase() === 'resubscribe') {
					shouldNotText = false;
				}
				state.doNotText = shouldNotText;
			}
		}
	}
});

export default candidateSlice;
